import { ProposalData } from './types'

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.max(
    0,
    Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  )
}

const nihChecklistBase = (
  overrides?: { label: string; passed: boolean }[],
) => ({
  label: 'NIH PDF Attachment Formatting',
  passed: false,
  isChecklistRef: true as const,
  checklistName: 'NIH PDF Attachment Formatting',
  ...(overrides ? { checklistOverrides: overrides } : {}),
})

const nihAllPass = [
  { label: 'Document is provided as a single PDF file.', passed: true },
  { label: 'Paper size is US Letter (8.5" × 11").', passed: true },
  { label: 'Margins are at least 0.5 inches on all sides.', passed: true },
  {
    label: 'No text, figures, or other content appears in the margins.',
    passed: true,
  },
  {
    label:
      'Font family is an NIH-accepted font (Arial, Helvetica, Palatino Linotype, or Georgia).',
    passed: true,
  },
  { label: 'Font color is black.', passed: true },
  {
    label:
      'Font size is 11 pt or larger (except figure labels that remain legible at 100% zoom).',
    passed: true,
  },
  {
    label:
      'Type density is no more than 15 characters per inch (including spaces).',
    passed: true,
  },
  {
    label: 'Line spacing is no more than 6 lines per vertical inch.',
    passed: false,
  },
  {
    label:
      'No headers or footers are included in the uploaded PDF (NIH systems add them during assembly).',
    passed: true,
  },
  {
    label:
      'File name is ≤ 50 characters and uses only standard characters (letters, numbers, underscore, hyphen, space, period).',
    passed: true,
  },
]

const nihStrategyChecks = [
  { label: 'Document is provided as a single PDF file.', passed: true },
  { label: 'Paper size is US Letter (8.5" × 11").', passed: true },
  { label: 'Margins are at least 0.5 inches on all sides.', passed: true },
  {
    label: 'No text, figures, or other content appears in the margins.',
    passed: true,
  },
  {
    label:
      'Font family is an NIH-accepted font (Arial, Helvetica, Palatino Linotype, or Georgia).',
    passed: true,
  },
  { label: 'Font color is black.', passed: true },
  {
    label:
      'Font size is 11 pt or larger (except figure labels that remain legible at 100% zoom).',
    passed: false,
  },
  {
    label:
      'Type density is no more than 15 characters per inch (including spaces).',
    passed: false,
  },
  {
    label: 'Line spacing is no more than 6 lines per vertical inch.',
    passed: false,
  },
  {
    label:
      'No headers or footers are included in the uploaded PDF (NIH systems add them during assembly).',
    passed: true,
  },
  {
    label:
      'File name is ≤ 50 characters and uses only standard characters (letters, numbers, underscore, hyphen, space, period).',
    passed: true,
  },
]

const rfaUrl =
  'https://grants.nih.gov/grants/guide/rfa-files/RFA-EY-13-001.html'
const pageLimitsUrl =
  'https://grants.nih.gov/grants-process/write-application/how-to-apply-application-guide/page-limits'

export const freshProposalFixture: ProposalData = {
  header: {
    title: 'NIH R21: Multisensory Study',
    funder: 'NIH',
    mechanism: 'R21',
    foaNumber: 'RFA-EY-13-001',
    piName: 'Dr. Sarah Chen',
    proposalStatus: 'On Track',
    overallProgress: 0,
    daysLeft: daysUntil('2026-06-01'),
    deadline: 'Jun 1, 2026',
    team: [
      {
        initials: 'SC',
        name: 'Dr. Sarah Chen',
        institution: 'UC San Francisco',
        color: 'bg-indigo-100 text-indigo-700',
      },
      {
        initials: 'MS',
        name: 'Maria Santos',
        institution: 'UC San Francisco',
        color: 'bg-emerald-100 text-emerald-700',
      },
    ],
    riskStatus: 'on-track',
    riskSummary: 'Proposal setup complete. No risks identified yet.',
    riskSignals: [],
  },
  milestones: [
    {
      id: 'ms_int_create_in_origami',
      title: 'Create proposal in Origami',
      date: 'Feb 18, 2026',
      status: 'completed',
      type: 'internal',
      source: 'manual',
    },
    {
      id: 'ms_int_notify_spo_of_nih_loi',
      title: 'Notify SPO of planned NIH LOI',
      date: 'Apr 13, 2026',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'SPO Policy: Sponsor communications + key date awareness',
      note: 'SPO requires awareness of any sponsor-facing communications and key dates before submission.',
    },
    {
      id: 'ms_int_open_proposal_with_spo',
      title: 'Open proposal with SPO (intake form)',
      date: 'Apr 15, 2026',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'SPO Policy: Intake required to open a proposal record',
      note: 'Required to create the institutional record.',
    },
    {
      id: 'ms_ext_foa_loi_due',
      title: 'Submit LOI to NIH',
      date: 'Apr 20, 2026',
      status: 'upcoming',
      type: 'external',
      source: 'ai-extracted',
      sourceRef: 'FOA Key Dates — Letter of Intent due date',
    },
    {
      id: 'ms_int_complete_internal_scientific_review',
      title: 'Complete internal scientific review',
      date: 'May 20, 2026',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'SPO Policy: Internal review window',
      note: 'Typical departmental/center review due ~10 business days before SPO final review.',
    },
    {
      id: 'ms_int_complete_spo_final_review',
      title: 'Complete SPO final review',
      date: 'May 27, 2026',
      status: 'upcoming',
      type: 'internal',
      source: 'policy',
      sourceRef: 'SPO Policy: Final review deadline',
      note: 'SPO requires final internal review 3–4 business days before Grants.gov submission.',
    },
    {
      id: 'ms_ext_submit_to_grants_gov',
      title: 'Submit application to Grants.gov',
      date: 'May 29, 2026',
      status: 'upcoming',
      type: 'external',
      source: 'policy',
      sourceRef: 'SPO Policy: Grants.gov buffer',
      note: 'Submit 2 business days before NIH receipt date.',
    },
    {
      id: 'ms_ext_nih_receipt',
      title: 'Meet NIH application receipt deadline',
      date: 'Jun 1, 2026',
      status: 'upcoming',
      type: 'external',
      source: 'ai-extracted',
      sourceRef: 'FOA due date — Application due by 5:00 PM local time',
    },
  ],
  criteria: [
    {
      id: 'C-SCOPE-01',
      title:
        'Project aligns with FOA purpose: basic behavioral research on multisensory processing.',
      type: 'Scope',
      sourceRef: 'RFA-EY-13-001: Funding Opportunity Description (Purpose)',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-SCOPE-02',
      title:
        'Proposed work examines multisensory processing across two or more sensory modalities.',
      type: 'Scope',
      sourceRef: 'RFA-EY-13-001: Scope and Requirements',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-SCOPE-AOI-01',
      title:
        "Addresses one or more FOA 'Specific Areas of Research Interest' examples.",
      type: 'Scope',
      sourceRef: 'RFA-EY-13-001: Specific Areas of Research Interest',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-SCOPE-03',
      title:
        "Application is responsive to OppNet's NIH definition of basic behavioral and social science research (b-BSSR).",
      type: 'Scope',
      sourceRef: 'RFA-EY-13-001: About OppNet',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-DQ-01',
      title:
        'Non-responsive/disqualifying: project examines only one sensory modality.',
      type: 'Disqualifications',
      sourceRef: 'RFA-EY-13-001: Non-responsive Applications',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-DQ-02',
      title:
        'Non-responsive/disqualifying: research on pain perception as the focus.',
      type: 'Disqualifications',
      sourceRef: 'RFA-EY-13-001: Non-responsive Applications (Pain)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-DQ-03',
      title:
        'Non-responsive/disqualifying: primary thrust is limited to neural circuits or neural mechanisms.',
      type: 'Disqualifications',
      sourceRef: 'RFA-EY-13-001: Non-responsive Applications (Neural)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-DQ-04',
      title:
        'Non-responsive/disqualifying: applied research tied to specific diseases/conditions as the primary focus.',
      type: 'Disqualifications',
      sourceRef: 'RFA-EY-13-001: Non-responsive Applications (Applied)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-ELIG-01',
      title: 'Application type must be New.',
      type: 'Eligibility',
      sourceRef: 'RFA-EY-13-001: Application Types Allowed',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-ELIG-02',
      title: 'Applicant organization is an eligible organization type.',
      type: 'Eligibility',
      sourceRef: 'RFA-EY-13-001: Eligibility Information',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-ELIG-03',
      title:
        'Multiple applications from the same organization are allowed only if each is scientifically distinct.',
      type: 'Eligibility',
      sourceRef: 'RFA-EY-13-001: Number of Applications',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-ELIG-04',
      title:
        'NIH will not accept an application essentially the same as one currently pending initial peer review.',
      type: 'Eligibility',
      sourceRef: 'RFA-EY-13-001: Similarity to Pending Application',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'manual-review',
      notes: 'Requires manual verification.',
    },
    {
      id: 'C-BUD-01',
      title: 'Project period may not exceed 2 years.',
      type: 'Budget',
      sourceRef: 'RFA-EY-13-001: Award Project Period',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-09-BudgetJustification'],
      status: 'manual-review',
      notes: 'Budget details require manual review.',
    },
    {
      id: 'C-BUD-02',
      title:
        'Total direct costs for the two-year project period may not exceed $275,000.',
      type: 'Budget',
      sourceRef: 'RFA-EY-13-001: Award Budget (Total Direct Cost Cap)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-09-BudgetJustification'],
      status: 'manual-review',
      notes: 'Budget details require manual review.',
    },
    {
      id: 'C-BUD-03',
      title: 'Direct costs may not exceed $200,000 in any single year.',
      type: 'Budget',
      sourceRef: 'RFA-EY-13-001: Award Budget (Single-Year Cap)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-09-BudgetJustification'],
      status: 'manual-review',
      notes: 'Budget details require manual review.',
    },
    {
      id: 'C-BUD-04',
      title: 'Direct costs must be requested in $25,000 modules.',
      type: 'Budget',
      sourceRef: 'RFA-EY-13-001: Award Budget (Modular)',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-09-BudgetJustification'],
      status: 'manual-review',
      notes: 'Budget details require manual review.',
    },
    {
      id: 'C-REV-01',
      title: 'Resource Sharing Plans are required.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Review Criteria / Resource Sharing Plans',
      sourceUrl: rfaUrl,
      artifact: ['A-14-ResourceSharingPlans'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-02',
      title:
        'Budget and requested period of support will be evaluated for reasonableness.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Additional Review Considerations (Budget)',
      sourceUrl: rfaUrl,
      artifact: ['A-09-BudgetJustification', 'Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-03',
      title: 'If human subjects are involved, protections are evaluated.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Additional Review Criteria (Human Subjects)',
      sourceUrl: rfaUrl,
      artifact: ['A-13-HumanSubjects'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-04',
      title: 'If clinical research is involved, inclusion plans are evaluated.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Additional Review Criteria (Inclusion Plans)',
      sourceUrl: rfaUrl,
      artifact: ['A-13-HumanSubjects'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-05',
      title:
        'If vertebrate animals are involved, review will evaluate the Vertebrate Animals section.',
      type: 'Explicit',
      sourceRef:
        'RFA-EY-13-001: Additional Review Criteria (Vertebrate Animals)',
      sourceUrl: rfaUrl,
      artifact: ['A-17-VertebrateAnimals'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-06',
      title: 'If biohazards are involved, reviewers will assess protections.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Additional Review Criteria (Biohazards)',
      sourceUrl: rfaUrl,
      artifact: ['A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-07',
      title:
        'If select agents are involved, reviewers will assess registration and safety plans.',
      type: 'Explicit',
      sourceRef:
        'RFA-EY-13-001: Additional Review Considerations (Select Agent)',
      sourceUrl: rfaUrl,
      artifact: ['A-18-SelectAgent'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-REV-08',
      title:
        'For foreign organizations, reviewers will consider special opportunities.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Additional Review Considerations (Foreign)',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'manual-review',
      notes: 'Requires manual review.',
    },
    {
      id: 'C-ADMIN-01',
      title: 'All NIH page limitations must be followed.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Page Limitations',
      sourceUrl: rfaUrl,
      artifact: [
        'Overall',
        'A-10-SpecificAims',
        'A-11-ResearchStrategy',
        'A-03-ProjectSummary',
        'A-04-ProjectNarrative',
      ],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-ADMIN-02',
      title: 'Appendix must not be used to circumvent page limits.',
      type: 'Explicit',
      sourceRef: 'RFA-EY-13-001: Appendix Guidance',
      sourceUrl: rfaUrl,
      artifact: ['A-21-Appendix'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-IMPL-01',
      title: 'Letter of intent is encouraged (not required).',
      type: 'Implicit',
      sourceRef: 'RFA-EY-13-001: Letter of Intent',
      sourceUrl: rfaUrl,
      artifact: ['Overall'],
      status: 'not-checked',
      notes: '',
    },
    {
      id: 'C-IMPL-02',
      title:
        'R21 submissions should reflect an exploratory/developmental approach; preliminary data are not required.',
      type: 'Implicit',
      sourceRef: 'RFA-EY-13-001: R21 Guidance',
      sourceUrl: rfaUrl,
      artifact: ['Overall', 'A-10-SpecificAims', 'A-11-ResearchStrategy'],
      status: 'not-checked',
      notes: '',
    },
  ],
  artifactCategories: [
    {
      id: 'submission',
      label: 'Submission & Forms',
      iconName: 'FolderOpen',
      completedCount: 0,
      totalCount: 2,
      artifacts: [
        {
          id: 'A-01-SubmissionPackage',
          title: 'Electronic Submission Package SF424 (R&R) Form',
          complianceStatus: 'manual-review',
          citation: '[RFA-EY-13-001]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Application is prepared for electronic submission.',
              passed: false,
              sourceUrls: [rfaUrl],
            },
            {
              label:
                'The package governed by the SF424 (R&R) Application Guide.',
              passed: false,
              sourceUrls: [rfaUrl],
            },
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-02-CoverLetter',
          title: 'Cover Letter',
          complianceStatus: 'not-checked',
          citation: '[NIH PDF Formatting]',
          requirementStatus: 'Conditional',
          compliance: [
            { label: 'Document is a single letter-style PDF.', passed: false },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'project-overview',
      label: 'Project Overview',
      iconName: 'FolderOpen',
      completedCount: 0,
      totalCount: 2,
      artifacts: [
        {
          id: 'A-03-ProjectSummary',
          title: 'Project Summary / Abstract',
          complianceStatus: 'not-checked',
          citation: '[NIH Page Limits]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Length is limited to 30 lines of text.',
              passed: false,
              sourceUrls: [pageLimitsUrl],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-04-ProjectNarrative',
          title: 'Project Narrative (Public Health Relevance)',
          complianceStatus: 'not-checked',
          citation: '[NIH Page Limits]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Length is limited to three sentences.',
              passed: false,
              sourceUrls: [pageLimitsUrl],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'personnel',
      label: 'Personnel',
      iconName: 'Users',
      completedCount: 0,
      totalCount: 3,
      artifacts: [
        {
          id: 'A-07-BiosketchBundle',
          title: 'Biosketches (one PDF per Senior/Key Person)',
          complianceStatus: 'not-checked',
          citation: '[NIH Biosketch]',
          requirementStatus: 'Required',
          compliance: [
            {
              label:
                'A biosketch document is provided for each Senior/Key Person.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants-process/write-application/forms-directory/biosketch',
              ],
            },
            {
              label:
                'Each biosketch is in the NIH Biographical Sketch Common Form format.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants-process/write-application/forms-directory/biosketch',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'research-plan',
      label: 'Research Plan',
      iconName: 'Beaker',
      completedCount: 0,
      totalCount: 10,
      artifacts: [
        {
          id: 'A-10-SpecificAims',
          title: 'Specific Aims',
          complianceStatus: 'not-checked',
          citation: '[NIH Page Limits]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Page limit: 1 page maximum.',
              passed: false,
              sourceUrls: [pageLimitsUrl],
            },
            nihChecklistBase(nihAllPass),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-11-ResearchStrategy',
          title: 'Research Strategy',
          complianceStatus: 'not-checked',
          citation: '[NIH Page Limits]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Page limit: 6 pages maximum for R21 Research Strategy.',
              passed: false,
              sourceUrls: [pageLimitsUrl],
            },
            nihChecklistBase(nihStrategyChecks),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-12-References',
          title: 'Bibliography & References Cited',
          complianceStatus: 'not-checked',
          citation: '[R&R Other Project Information]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'References are provided as a single PDF document.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants/how-to-apply-application-guide/forms-i/general/g.220-r%26r-other-project-information-form.htm',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-14-ResourceSharingPlans',
          title: 'Resource Sharing Plans (single PDF)',
          complianceStatus: 'not-checked',
          citation: '[RFA-EY-13-001]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'A single Resource Sharing Plans PDF is provided.',
              passed: false,
              sourceUrls: [rfaUrl],
            },
            {
              label:
                'Document includes labeled sections for: Data Sharing Plan, Model Organism Sharing Plan, and GWAS plan.',
              passed: false,
              sourceUrls: [rfaUrl],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-15-DMSPlan',
          title: 'Data Management & Sharing (DMS) Plan (PDF)',
          complianceStatus: 'not-checked',
          citation: '[NIH DMS Policy]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If the project will generate scientific data, a DMS Plan is provided.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/policy-and-compliance/policy-topics/sharing-policies/dms/policy-overview',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-16-Authentication',
          title:
            'Authentication of Key Biological and/or Chemical Resources (PDF)',
          complianceStatus: 'not-checked',
          citation: '[NOT-OD-17-068]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If established key biological/chemical resources are used, an Authentication document is provided.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants/guide/notice-files/NOT-OD-17-068.html',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-17-VertebrateAnimals',
          title: 'Vertebrate Animals (PDF)',
          complianceStatus: 'not-checked',
          citation: '[PHS 398 Research Plan]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If vertebrate animals are involved, a Vertebrate Animals document is provided.',
              passed: false,
              sourceUrls: [
                'https://apply07.grants.gov/apply/forms/readonly/PHS398_ResearchPlan_5_0-V5.0.pdf',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-18-SelectAgent',
          title: 'Select Agent Research (PDF)',
          complianceStatus: 'not-checked',
          citation: '[PHS 398 Research Plan]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If select agents are involved, a Select Agent Research document is provided.',
              passed: false,
              sourceUrls: [
                'https://apply07.grants.gov/apply/forms/readonly/PHS398_ResearchPlan_5_0-V5.0.pdf',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-19-MultiplePDPIPlan',
          title: 'Multiple PD/PI Leadership Plan (PDF)',
          complianceStatus: 'not-checked',
          citation: '[PHS 398 Research Plan]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If multiple PD/PIs are designated, a Multiple PD/PI Leadership Plan document is provided.',
              passed: false,
              sourceUrls: [
                'https://apply07.grants.gov/apply/forms/readonly/PHS398_ResearchPlan_5_0-V5.0.pdf',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-20-ConsortiumArrangements',
          title: 'Consortium/Contractual Arrangements (PDF)',
          complianceStatus: 'not-checked',
          citation: '[PHS 398 Research Plan]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If consortium/subawards are part of the application, a Consortium/Contractual Arrangements document is provided.',
              passed: false,
              sourceUrls: [
                'https://apply07.grants.gov/apply/forms/readonly/PHS398_ResearchPlan_5_0-V5.0.pdf',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'budget',
      label: 'Budget',
      iconName: 'DollarSign',
      completedCount: 0,
      totalCount: 1,
      artifacts: [
        {
          id: 'A-09-BudgetJustification',
          title: 'PHS 398 Modular Budget + Budget Justification',
          complianceStatus: 'manual-review',
          citation: '[PHS 398 Modular Budget]',
          requirementStatus: 'Required',
          compliance: [
            {
              label: 'Budget uses the PHS 398 Modular Budget Form.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants/how-to-apply-application-guide/forms-i/general/g.320-phs-398-modular-budget-form.htm',
              ],
            },
            {
              label:
                'Budget Justification is provided as a single PDF document.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants/how-to-apply-application-guide/forms-i/general/g.320-phs-398-modular-budget-form.htm',
              ],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
    {
      id: 'supporting',
      label: 'Supporting Documents',
      iconName: 'FolderOpen',
      completedCount: 0,
      totalCount: 2,
      artifacts: [
        {
          id: 'A-05-FacilitiesOtherResources',
          title: 'Facilities & Other Resources',
          complianceStatus: 'not-checked',
          citation: '[NIH PDF Formatting]',
          requirementStatus: 'Required',
          compliance: [nihChecklistBase()],
          hasFile: false,
          readyStatus: false,
        },
        {
          id: 'A-21-Appendix',
          title: 'Appendix (PDF)',
          complianceStatus: 'not-checked',
          citation: '[NOT-OD-17-098]',
          requirementStatus: 'Conditional',
          compliance: [
            {
              label:
                'If included, Appendix is provided as a standalone PDF document.',
              passed: false,
              sourceUrls: [
                'https://grants.nih.gov/grants/guide/notice-files/NOT-OD-17-098.html',
              ],
            },
            {
              label:
                'Appendix document does not contain content intended to bypass Research Plan page limits.',
              passed: false,
              sourceUrls: [rfaUrl],
            },
            nihChecklistBase(),
          ],
          hasFile: false,
          readyStatus: false,
        },
      ],
    },
  ],
  grantsmanshipSections: [],
  tasks: [],
}
